// Bulk customer + nominee import for the Pawn Customer Master screen.
//
// Modelled on the "Create Multiple New Orders" flow of the order app
// (order_app_frontend/src/pages/ExcelOrdersCreation.js): download a
// self-documenting template, upload a filled sheet, then create the records one
// row at a time against the normal single-record APIs — so a bulk import lands
// exactly the rows a hand-typed customer would. Written for migrating an old
// on-prem pawn app's customer list into the cloud module.
//
// A nominee is a customer too; only the Type / Parent Mobile columns make them
// one. So the import runs in two phases: every customer row is POSTed first,
// then every nominee is attached to its parent with nominees_to_add. That way a
// nominee may sit above its customer in the sheet, and by the time the links are
// made every parent is guaranteed to exist.
//
// The sheet contract itself (columns, template, parsing, analysis) lives in
// ./bulkCustomerSheet so it stays testable without React.
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Link,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  keyframes,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import GroupAddRoundedIcon from "@mui/icons-material/GroupAddRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import DiversityRoundedIcon from "@mui/icons-material/Diversity3Rounded";
import * as XLSX from "xlsx";
import axios from "axios";

import { PRIMARY, API_BASE_URL } from "./shared";
import {
  COLUMNS,
  DATA_SHEET_NAME,
  analyseRecords,
  buildRecords,
  downloadCustomerTemplate,
  downloadRowReport,
  extractRows,
  mobilesToVerify,
  oldId,
  partyCodesToVerify,
  parentAadharsToVerify,
} from "./bulkCustomerSheet";

// How many duplicate look-ups run at once during the pre-flight check.
const LOOKUP_CONCURRENCY = 10;

// ─── Animations ───────────────────────────────────────────────────────────────
const pop = keyframes`
  0%   { transform: scale(1);    }
  40%  { transform: scale(1.22); }
  100% { transform: scale(1);    }
`;
const riseIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0);   }
`;
const shimmer = keyframes`
  0%   { background-position: -420px 0; }
  100% { background-position:  420px 0; }
`;
const dots = keyframes`
  0%, 20%  { content: "."; }
  40%      { content: ".."; }
  60%      { content: "..."; }
  80%, 100%{ content: ""; }
`;

// ─── Live counter tile ────────────────────────────────────────────────────────
// The number is keyed on its own value so React remounts it on every change and
// the pop animation re-fires — that tick is what makes the count feel live.
const StatTile = ({ label, value, tone = "slate", icon, delay = 0 }) => {
  const tones = {
    slate: { fg: "#334155", bg: "#F1F5F9", br: "#E2E8F0" },
    blue: { fg: PRIMARY, bg: `${PRIMARY}12`, br: `${PRIMARY}33` },
    green: { fg: "#15803D", bg: "#ECFDF5", br: "#A7F3D0" },
    red: { fg: "#B91C1C", bg: "#FEF2F2", br: "#FECACA" },
    amber: { fg: "#B45309", bg: "#FFFBEB", br: "#FDE68A" },
  };
  const t = tones[tone] || tones.slate;

  return (
    <Box
      sx={{
        flex: "1 1 96px",
        minWidth: 96,
        px: 1.25,
        py: 1,
        borderRadius: 2.5,
        bgcolor: t.bg,
        border: `1px solid ${t.br}`,
        textAlign: "center",
        animation: `${riseIn} 0.35s ease both`,
        animationDelay: `${delay}ms`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.5,
        }}
      >
        {icon
          ? React.cloneElement(icon, {
              sx: { fontSize: 15, color: t.fg, opacity: 0.85 },
            })
          : null}
        <Typography
          key={value}
          component="span"
          sx={{
            fontSize: "1.35rem",
            fontWeight: 800,
            lineHeight: 1.1,
            color: t.fg,
            display: "inline-block",
            animation: `${pop} 0.45s ease`,
          }}
        >
          {value}
        </Typography>
      </Box>
      <Typography
        sx={{
          fontSize: "0.66rem",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: 0.4,
          color: t.fg,
          opacity: 0.8,
          mt: 0.25,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

// ─── Tile group ───────────────────────────────────────────────────────────────
// Counts only mean something once it is obvious which ones add up together, so
// the "will be created" tiles and the "will be skipped" tiles sit in separate
// labelled boxes instead of one undifferentiated row.
const TileGroup = ({ label, tone, children }) => {
  const edge = tone === "red" ? "#FECACA" : "#A7F3D0";
  const fg = tone === "red" ? "#B91C1C" : "#15803D";
  return (
    <Box
      sx={{
        flex: "1 1 260px",
        minWidth: 240,
        p: 1,
        borderRadius: 3,
        border: `1px solid ${edge}`,
        bgcolor: "#fff",
      }}
    >
      <Typography
        sx={{
          fontSize: "0.66rem",
          fontWeight: 800,
          textTransform: "uppercase",
          letterSpacing: 0.6,
          color: fg,
          mb: 0.75,
          ml: 0.25,
        }}
      >
        {label}
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {children}
      </Stack>
    </Box>
  );
};

// ─── Percentage ring ──────────────────────────────────────────────────────────
const ProgressRing = ({ value, size = 84 }) => (
  <Box sx={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
    <CircularProgress
      variant="determinate"
      value={100}
      size={size}
      thickness={4.5}
      sx={{ color: `${PRIMARY}1F`, position: "absolute", left: 0 }}
    />
    <CircularProgress
      variant="determinate"
      value={value}
      size={size}
      thickness={4.5}
      sx={{
        color: PRIMARY,
        position: "absolute",
        left: 0,
        "& .MuiCircularProgress-circle": {
          strokeLinecap: "round",
          transition: "stroke-dashoffset 0.4s ease",
        },
      }}
    />
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Typography sx={{ fontSize: "1.05rem", fontWeight: 800, color: PRIMARY }}>
        {Math.round(value)}%
      </Typography>
    </Box>
  </Box>
);

const EMPTY_PROGRESS = {
  phase: "idle", // idle | customers | nominees
  total: 0,
  done: 0,
  custTotal: 0,
  custOk: 0,
  custFail: 0,
  nomTotal: 0,
  nomOk: 0,
  nomFail: 0,
  current: "",
};

// ─── Component ────────────────────────────────────────────────────────────────
const BulkCustomerDialog = ({
  open,
  onClose,
  storeId,
  createdBy,
  onImported,
  // The store's "Allow duplicate mobile numbers" setting. Without it the server
  // refuses a repeated number, so the override switch is not even offered.
  allowDuplicateMobile = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const fileInputRef = useRef(null);

  const [fileName, setFileName] = useState("");
  const [isParsing, setIsParsing] = useState(false);
  const [lookupProgress, setLookupProgress] = useState(null); // { done, total }
  const [parseError, setParseError] = useState("");
  const [parsed, setParsed] = useState(null);
  // { sheetName, matchedByHeader, records, customers, nominees, skipped }

  const [isCreating, setIsCreating] = useState(false);
  const [progress, setProgress] = useState(EMPTY_PROGRESS);
  const [result, setResult] = useState(null);
  // { custOk, nomOk, failures: [] }

  // The affirmative "here is exactly what will be created" list, collapsed by
  // default so a 200-row sheet does not bury the problem tables.
  const [showCreateList, setShowCreateList] = useState(false);

  // The user's answer to "these rows repeat a mobile number — create them
  // anyway?". Only offered when the store's own setting permits it, otherwise
  // every such row would be created here and then refused by the API.
  const [importDuplicates, setImportDuplicates] = useState(false);

  // The analysis is derived, not stored: flipping the duplicate switch re-runs
  // it against the same parsed rows and look-ups, with no new requests.
  const analysis = useMemo(
    () =>
      parsed
        ? analyseRecords(parsed.records, {
            ...parsed.lookups,
            allowDuplicateMobile: importDuplicates,
          })
        : null,
    [parsed, importDuplicates],
  );

  const skipped = useMemo(() => analysis?.skipped || [], [analysis]);
  // Three buckets, because they are not equally fixable: a repeated mobile is a
  // policy question, a repeated party code always fails (real unique index),
  // and everything else is a data error in the row.
  const mobileDuplicates = useMemo(
    () => skipped.filter((s) => s.category === "duplicate-mobile"),
    [skipped],
  );
  const partyDuplicates = useMemo(
    () => skipped.filter((s) => s.category === "duplicate-party"),
    [skipped],
  );
  // Its own bucket, and NOT a problem: a row whose Old Customer ID is already
  // in the app means that person was migrated on an earlier run. Lumping it in
  // with data errors would tell a shop re-running a part-finished sheet to go
  // and fix rows that are already perfectly in.
  const alreadyImported = useMemo(
    () => skipped.filter((s) => s.category === "already-imported"),
    [skipped],
  );
  const otherProblems = useMemo(
    () =>
      skipped.filter(
        (s) =>
          s.category !== "duplicate-mobile" &&
          s.category !== "duplicate-party" &&
          s.category !== "already-imported",
      ),
    [skipped],
  );
  const warned = useMemo(
    () =>
      [...(analysis?.customers || []), ...(analysis?.nominees || [])].filter(
        (r) => r.warnings.length > 0,
      ),
    [analysis],
  );
  const workTotal =
    (analysis?.customers?.length || 0) + (analysis?.nominees?.length || 0);

  // How many rows are being imported onto a number somebody else also uses.
  // Only meaningful while importDuplicates is on — before that they are skips.
  const sharedMobileCount = useMemo(
    () =>
      (analysis?.customers || []).filter((r) =>
        r.warnings.some((w) => w.startsWith("Mobile ")),
      ).length,
    [analysis],
  );

  const resetAll = () => {
    setFileName("");
    setParseError("");
    setParsed(null);
    setResult(null);
    setLookupProgress(null);
    setProgress(EMPTY_PROGRESS);
    setShowCreateList(false);
    setImportDuplicates(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // The dialog stays mounted between openings, so start every visit clean —
  // otherwise the previous import's result panel is still sitting there.
  useEffect(() => {
    if (open) resetAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    if (isCreating) return; // never abandon a half-finished import
    onClose();
  };

  const readWorkbook = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error("The file could not be read."));
      reader.onload = (e) => {
        try {
          resolve(
            XLSX.read(new Uint8Array(e.target.result), {
              type: "array",
              cellDates: true,
            }),
          );
        } catch (err) {
          reject(err);
        }
      };
      reader.readAsArrayBuffer(file);
    });

  // Which of the sheet's mobile numbers already exist in this store — asked of
  // check-duplicate rather than the customer list, because the list endpoint only
  // returns cust_role 'primary' and would miss every existing nominee.
  // Runs `fetchOne` over `items` in small parallel batches, feeding the shared
  // pre-flight counter. `fetchOne` resolves to { key, value } or null.
  const lookupInBatches = async (items, fetchOne, offset, grandTotal) => {
    const found = new Map();
    for (let i = 0; i < items.length; i += LOOKUP_CONCURRENCY) {
      const batch = items.slice(i, i + LOOKUP_CONCURRENCY);
      const results = await Promise.all(batch.map(fetchOne));
      results
        .filter(Boolean)
        .forEach(({ key, value }) => found.set(key, value));
      setLookupProgress({
        done: offset + Math.min(i + batch.length, items.length),
        total: grandTotal,
      });
    }
    return found;
  };

  // A look-up that fails must not silently pass a row off as new — the server's
  // own guards are still the final word, so the row goes through and fails there
  // with a clear message instead.
  // Returns every holder of the number, not just one: a store that allows a
  // shared mobile can have several people on it, and the row needs to know that.
  const fetchMobileHolders = async (mobile) => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/pawn/customers/check-duplicate/${storeId}`,
        { params: { numbers: mobile } },
      );
      const list = res.data?.data?.existingCustomers;
      const holders = Array.isArray(list)
        ? list
        : // Older backend: single object only.
          [res.data?.data?.existingCustomer].filter(Boolean);
      return holders.length
        ? {
            key: mobile,
            value: holders.map((h) => ({ id: h.id, name: h.name })),
          }
        : null;
    } catch (err) {
      console.error("Mobile look-up failed for", mobile, err);
      return null;
    }
  };

  // Resolves a nominee's Parent Aadhar. Returns the full list — Aadhar carries
  // no unique index, so "matches two people" is a real answer the row must see.
  const fetchAadharHolders = async (aadhar) => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/pawn/customers/store/${storeId}/document/${encodeURIComponent(
          aadhar,
        )}`,
        { params: { name: "Aadhar" } },
      );
      const list = Array.isArray(res.data?.data) ? res.data.data : [];
      return list.length
        ? {
            key: aadhar,
            value: list.map((h) => ({
              id: h.customer_id ?? h.id,
              name: h.name,
            })),
          }
        : null;
    } catch (err) {
      console.error("Aadhar look-up failed for", aadhar, err);
      return null;
    }
  };

  // Party codes carry a per-store unique index, so a code already in use fails
  // the API outright. Checked here so the pre-flight does not promise a row that
  // is certain to fail.
  const fetchPartyCodeHolder = async (code) => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/pawn/customers/store/${storeId}/party/${encodeURIComponent(
          code,
        )}`,
      );
      const hit = res.data?.success ? res.data?.data : null;
      return hit
        ? {
            key: code,
            value: { id: hit.customer_id ?? hit.id, name: hit.name },
          }
        : null;
    } catch (err) {
      console.error("Party code look-up failed for", code, err);
      return null;
    }
  };

  // Every old-app id already migrated into this store, in ONE request rather
  // than one per value like the checks above. A migration sheet carries
  // thousands of old ids and they all answer the same question, so fanning out
  // would be thousands of round trips; the reply only covers rows that actually
  // carry an old_id, so it stays small however long the customer list is.
  const fetchExistingOldIds = async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/pawn/customers/store/${storeId}/old-ids`,
      );
      if (!res.data?.success) return new Map();
      const map = new Map();
      for (const row of res.data.data || []) {
        const key = oldId(row.old_id);
        if (key && !map.has(key))
          map.set(key, { id: row.id, name: row.name, status: row.status });
      }
      return map;
    } catch (err) {
      // Not fatal: the create call still refuses a duplicate, and its 409 now
      // names the holder. This only costs the pre-flight its warning.
      console.error("Old-id look-up failed", err);
      return new Map();
    }
  };

  const handleFilePicked = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    setParseError("");
    setParsed(null);
    setResult(null);
    setIsParsing(true);

    try {
      const workbook = await readWorkbook(file);
      const extracted = extractRows(workbook);

      if (!extracted.dataRows.length) {
        setParseError(
          `No rows found in "${file.name}". Fill your rows into the "${DATA_SHEET_NAME}" sheet of the template and upload it again. (The "Sample Data" sheet is never imported.)`,
        );
        return;
      }

      const records = buildRecords(extracted);

      const mobiles = mobilesToVerify(records);
      const partyCodes = partyCodesToVerify(records);
      const aadhars = parentAadharsToVerify(records);
      const grandTotal = mobiles.length + partyCodes.length + aadhars.length;
      setLookupProgress({ done: 0, total: grandTotal });

      const existingByMobile = await lookupInBatches(
        mobiles,
        fetchMobileHolders,
        0,
        grandTotal,
      );
      const existingPartyCodes = await lookupInBatches(
        partyCodes,
        fetchPartyCodeHolder,
        mobiles.length,
        grandTotal,
      );
      const existingByAadhar = await lookupInBatches(
        aadhars,
        fetchAadharHolders,
        mobiles.length + partyCodes.length,
        grandTotal,
      );
      const existingOldIds = await fetchExistingOldIds();
      setLookupProgress(null);

      // Only the raw material is stored — the analysis is derived, so the
      // duplicate switch can re-run it without touching the network again.
      setParsed({
        sheetName: extracted.sheetName,
        matchedByHeader: !!extracted.headerIndexes,
        records,
        lookups: {
          existingByMobile,
          existingPartyCodes,
          existingByAadhar,
          existingOldIds,
        },
      });
    } catch (err) {
      console.error("Bulk customer parse failed:", err);
      setParseError(
        "That file could not be opened as a spreadsheet. Save it as .xlsx or .csv and try again.",
      );
    } finally {
      setIsParsing(false);
    }
  };

  // Phase 1 creates every customer against the normal single-create API, so a
  // failure is isolated to its own row. Phase 2 attaches each nominee to its
  // parent — by then every in-sheet parent has an id.
  const handleCreate = async () => {
    // From `analysis`, not `parsed`: the work lists are derived so that the
    // duplicate switch can change them. `parsed` only holds the raw rows and the
    // look-ups, so reading them from there silently found nothing to do.
    const { customers = [], nominees = [] } = analysis || {};
    if (!customers.length && !nominees.length) return;

    setIsCreating(true);
    setResult(null);
    setProgress({
      ...EMPTY_PROGRESS,
      phase: "customers",
      total: customers.length + nominees.length,
      custTotal: customers.length,
      nomTotal: nominees.length,
    });

    const failures = [];
    // Keyed by the parent's ROW, never by mobile: a nominee identified through
    // Parent Aadhar has no parent mobile at all, and two customers sharing one
    // number would collide and hand a nominee the wrong sibling.
    const newIdByRow = new Map(); // sheet row -> freshly created customer id
    let done = 0;
    let custOk = 0;
    let custFail = 0;
    let nomOk = 0;
    let nomFail = 0;

    const reasonOf = (err) =>
      err?.statusMessage ||
      err?.response?.data?.statusMessage ||
      err?.message ||
      "Unknown error";

    // ── Phase 1 · customers ───────────────────────────────────────────────────
    for (const record of customers) {
      setProgress((p) => ({
        ...p,
        phase: "customers",
        current: `${record.name} · row ${record.rowNo}`,
      }));
      try {
        const res = await axios.post(`${API_BASE_URL}/api/pawn/customers`, {
          ...record.person,
          store_id: storeId,
          created_by: createdBy,
          cust_role: "primary",
          nominees: [],
        });
        if (!res.data?.success) throw res.data;
        const newId = res.data?.data?.id ?? res.data?.data?.customer_id;
        if (newId) newIdByRow.set(record.rowNo, newId);
        custOk += 1;
      } catch (err) {
        custFail += 1;
        failures.push({
          rowNo: record.rowNo,
          cells: record.cells,
          kind: "customer",
          name: record.name,
          mobile: record.mobile,
          reason: reasonOf(err),
        });
      }
      done += 1;
      const tick = { done, custOk, custFail };
      setProgress((p) => ({ ...p, ...tick }));
    }

    // ── Phase 2 · nominee links ───────────────────────────────────────────────
    for (const nominee of nominees) {
      setProgress((p) => ({
        ...p,
        phase: "nominees",
        current: `${nominee.name} → ${nominee.parentName} · row ${nominee.rowNo}`,
      }));

      const parentId =
        nominee.parentExistingId ?? newIdByRow.get(nominee.parentRowNo);
      if (!parentId) {
        nomFail += 1;
        failures.push({
          rowNo: nominee.rowNo,
          cells: nominee.cells,
          kind: "nominee",
          name: nominee.name,
          mobile: nominee.mobile,
          reason: `Their customer "${nominee.parentName}"${
            nominee.parentRowNo ? ` on row ${nominee.parentRowNo}` : ""
          } could not be created, so there was nothing to link to`,
        });
        done += 1;
        const tick = { done, nomFail };
        setProgress((p) => ({ ...p, ...tick }));
        continue;
      }

      try {
        // Only nominees_to_add is sent: updateCustomer falls back to the stored
        // value for every field it does not receive, and skips document
        // processing entirely when `documents` is absent — so the parent's own
        // record is left exactly as it is.
        const res = await axios.put(
          `${API_BASE_URL}/api/pawn/customers/${parentId}`,
          {
            store_id: storeId,
            nominees_to_add: [
              {
                ...nominee.person,
                // Pins the person when the analysis already identified them, so
                // the server never has to guess from a shared mobile number.
                ...(nominee.nomineeExistingId
                  ? { nominee_id: nominee.nomineeExistingId }
                  : {}),
                relation: nominee.nomineeRelation || null,
                share_percent: null,
                is_primary: 0,
              },
            ],
          },
        );
        if (!res.data?.success) throw res.data;
        nomOk += 1;
      } catch (err) {
        nomFail += 1;
        failures.push({
          rowNo: nominee.rowNo,
          cells: nominee.cells,
          kind: "nominee",
          name: nominee.name,
          mobile: nominee.mobile,
          reason: reasonOf(err),
        });
      }
      done += 1;
      const tick = { done, nomOk, nomFail };
      setProgress((p) => ({ ...p, ...tick }));
    }

    setProgress((p) => ({ ...p, phase: "idle", current: "" }));
    setResult({ custOk, nomOk, failures });
    setIsCreating(false);
    if (custOk > 0 || nomOk > 0) onImported?.();
  };

  // ─── Row tables ─────────────────────────────────────────────────────────────
  // `lastCell` overrides the final column; it defaults to the row's own reason
  // or warning text, which is what the problem tables want.
  const RowTable = ({
    rows,
    lastHeader,
    maxHeight = 190,
    lastCell = (r) => r.reason || r.warnings?.join(" · "),
  }) => (
    <Box
      sx={{
        maxHeight,
        overflowY: "auto",
        overflowX: "auto",
        border: "1px solid #E2E8F0",
        borderRadius: 2,
        bgcolor: "#fff",
      }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, width: 62 }}>Row</TableCell>
            <TableCell sx={{ fontWeight: 700, width: 88 }}>Type</TableCell>
            <TableCell sx={{ fontWeight: 700, width: 168 }}>
              Name / Mobile
            </TableCell>
            <TableCell sx={{ fontWeight: 700 }}>{lastHeader}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={`${lastHeader}-${r.rowNo}`}>
              <TableCell sx={{ fontWeight: 700 }}>{r.rowNo}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={r.kind === "nominee" ? "Nominee" : "Customer"}
                  sx={{
                    height: 20,
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    bgcolor: r.kind === "nominee" ? "#EEF2FF" : "#F1F5F9",
                    color: r.kind === "nominee" ? "#4338CA" : "#334155",
                  }}
                />
              </TableCell>
              <TableCell sx={{ fontSize: "0.75rem" }}>
                {r.name || <em>(no name)</em>}
                {r.mobile ? (
                  <>
                    <br />
                    <Box
                      component="span"
                      sx={{ fontVariantNumeric: "tabular-nums", opacity: 0.75 }}
                    >
                      {r.mobile}
                    </Box>
                  </>
                ) : null}
              </TableCell>
              <TableCell sx={{ fontSize: "0.75rem" }}>{lastCell(r)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );

  const pct = progress.total ? (progress.done / progress.total) * 100 : 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3 } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          background:
            "linear-gradient(90deg, #1D4ED8 0%, #2563EB 50%, #3B82F6 100%)",
          color: "#fff",
          py: 1.75,
        }}
      >
        <GroupAddRoundedIcon />
        <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
          Bulk Create Customers &amp; Nominees
        </Typography>
        <IconButton
          size="small"
          onClick={handleClose}
          disabled={isCreating}
          sx={{ color: "#fff" }}
        >
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: "#F8FAFC" }}>
        {/* ── Step 1 · template ── */}
        <Box sx={{ mb: 2.5 }}>
          <Typography
            variant="subtitle2"
            fontWeight={700}
            sx={{ color: PRIMARY, mb: 0.75 }}
          >
            Step 1 · Get the format right
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <Typography
              variant="body2"
              sx={{ flex: 1, color: "text.secondary" }}
            >
              Download the template — it lists every column, what to fill in it,
              whether it is mandatory and where the value ends up. One sheet
              holds <b>both</b> customers and their nominees: set <b>Type</b> to{" "}
              <code>nominee</code> and put the customer's mobile in{" "}
              <b>Parent Mobile</b>. Its <b>Sample Data</b> sheet has a full
              worked example; type your own into <b>{DATA_SHEET_NAME}</b>.
            </Typography>
            <Button
              variant="outlined"
              startIcon={<DownloadRoundedIcon />}
              onClick={downloadCustomerTemplate}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 2,
                whiteSpace: "nowrap",
                borderColor: PRIMARY,
                color: PRIMARY,
              }}
            >
              Download template
            </Button>
          </Stack>
        </Box>

        <Divider sx={{ mb: 2.5 }} />

        {/* ── Step 2 · upload ── */}
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="subtitle2"
            fontWeight={700}
            sx={{ color: PRIMARY, mb: 0.75 }}
          >
            Step 2 · Upload the filled sheet
          </Typography>

          <input
            type="file"
            ref={fileInputRef}
            accept=".xlsx,.xls,.csv"
            hidden
            onChange={handleFilePicked}
          />

          <Box
            component="label"
            onClick={() => {
              if (!isCreating && !isParsing) fileInputRef.current?.click();
            }}
            sx={{
              display: "block",
              border: `2px dashed ${parsed ? PRIMARY : "#94A3B8"}`,
              borderRadius: 3,
              p: { xs: 2.5, md: 3.5 },
              textAlign: "center",
              bgcolor: parsed ? `${PRIMARY}08` : "#fff",
              cursor: isCreating || isParsing ? "not-allowed" : "pointer",
              opacity: isCreating ? 0.6 : 1,
              transition: "all 0.2s ease",
              "&:hover": { bgcolor: isCreating ? undefined : `${PRIMARY}0A` },
            }}
          >
            <UploadFileRoundedIcon
              sx={{ color: PRIMARY, fontSize: 34, mb: 0.5 }}
            />
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ color: PRIMARY }}
            >
              {fileName || "Click here to choose your Excel file"}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              .xlsx, .xls or .csv — no need to convert to CSV first
            </Typography>
          </Box>

          {(fileName || parsed) && !isCreating && (
            <Button
              size="small"
              color="error"
              startIcon={<DeleteOutlineRoundedIcon />}
              onClick={resetAll}
              sx={{ textTransform: "none", mt: 1 }}
            >
              Remove file
            </Button>
          )}
        </Box>

        {isParsing && (
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <CircularProgress size={18} />
            <Typography variant="body2">
              {lookupProgress
                ? `Checking mobile numbers and party codes against the app… ${lookupProgress.done} / ${lookupProgress.total}`
                : "Reading the sheet…"}
              <Box
                component="span"
                sx={{
                  "::after": {
                    content: '""',
                    animation: `${dots} 1.4s steps(1, end) infinite`,
                  },
                }}
              />
            </Typography>
          </Stack>
        )}

        {parseError && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {parseError}
          </Alert>
        )}

        {/* ── Step 3 · pre-flight ── */}
        {parsed && !result && !isCreating && (
          <Box sx={{ mb: 1 }}>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{ color: PRIMARY, mb: 1 }}
            >
              Step 3 · Check what was read
            </Typography>

            {/* One plain sentence first: what the sheet contained, and where
                the numbers below come from. */}
            <Typography variant="body2" sx={{ mb: 1.25 }}>
              <b>{parsed.records.length}</b> row
              {parsed.records.length === 1 ? "" : "s"} read from the{" "}
              <b>{parsed.sheetName}</b> sheet
              {analysis.skipped.length > 0 ? (
                <>
                  {" "}
                  — <b>{workTotal}</b> will be imported and{" "}
                  <b>{analysis.skipped.length}</b> skipped.
                </>
              ) : (
                <> — all of them will be imported.</>
              )}
              <Tooltip
                title={
                  parsed.matchedByHeader
                    ? "The header row was recognised, so columns were matched by their names."
                    : `No usable header row was found, so columns were read strictly by position A to ${
                        COLUMNS[COLUMNS.length - 1].letter
                      }.`
                }
              >
                <Chip
                  size="small"
                  variant="outlined"
                  label={
                    parsed.matchedByHeader
                      ? "Matched by header name"
                      : `Read by position (A–${
                          COLUMNS[COLUMNS.length - 1].letter
                        })`
                  }
                  sx={{ ml: 1, height: 20, fontSize: "0.68rem" }}
                />
              </Tooltip>
            </Typography>

            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              sx={{ mb: 1.25 }}
            >
              <TileGroup label="Will be created" tone="green">
                <StatTile
                  label="customers"
                  value={analysis.customers.length}
                  tone="green"
                  icon={<PersonRoundedIcon />}
                  delay={0}
                />
                <StatTile
                  label="nominees"
                  value={analysis.nominees.length}
                  tone="green"
                  icon={<DiversityRoundedIcon />}
                  delay={60}
                />
              </TileGroup>

              <TileGroup label="Will be skipped" tone="red">
                <StatTile
                  label={
                    importDuplicates ? "shared mobile" : "duplicate mobile"
                  }
                  value={
                    importDuplicates
                      ? sharedMobileCount
                      : mobileDuplicates.length
                  }
                  tone={
                    importDuplicates
                      ? "amber"
                      : mobileDuplicates.length
                      ? "red"
                      : "slate"
                  }
                  icon={<ContentCopyRoundedIcon />}
                  delay={120}
                />
                <StatTile
                  label="party code / bad field"
                  value={otherProblems.length + partyDuplicates.length}
                  tone={
                    otherProblems.length + partyDuplicates.length
                      ? "red"
                      : "slate"
                  }
                  delay={180}
                />
                {/* Kept as its own tile so the skipped tiles still add up to
                    the skipped total. Amber, never red: these rows are already
                    safely in the app, which is a good outcome, not a fault. */}
                <StatTile
                  label="already imported"
                  value={alreadyImported.length}
                  tone={alreadyImported.length ? "amber" : "slate"}
                  delay={240}
                />
              </TileGroup>
            </Stack>

            {/* The affirmative list. Without it the only tables on screen are
                about problems, which reads as though nothing else is imported. */}
            {workTotal > 0 && (
              <Box sx={{ mb: 1.5 }}>
                <Link
                  component="button"
                  type="button"
                  onClick={() => setShowCreateList((v) => !v)}
                  sx={{
                    fontSize: "0.8rem",
                    fontWeight: 700,
                    color: "#15803D",
                    textDecorationColor: "#15803D80",
                    display: "block",
                    mb: showCreateList ? 0.75 : 0,
                  }}
                >
                  {showCreateList ? "▾" : "▸"}{" "}
                  {showCreateList ? "Hide" : "Show"} the {workTotal} row
                  {workTotal === 1 ? "" : "s"} that will be imported
                </Link>
                <Collapse in={showCreateList} unmountOnExit>
                  <RowTable
                    rows={[...analysis.customers, ...analysis.nominees].sort(
                      (a, b) => a.rowNo - b.rowNo,
                    )}
                    lastHeader="What will be created"
                    maxHeight={230}
                    lastCell={(r) =>
                      r.kind === "nominee"
                        ? `Nominee of ${r.parentName}${
                            r.nomineeRelation ? ` (${r.nomineeRelation})` : ""
                          }`
                        : "New customer"
                    }
                  />
                </Collapse>
              </Box>
            )}

            {/* Repeated mobile numbers — the only skip reason the user can
                overrule, and only when the store's setting permits it. */}
            {(mobileDuplicates.length > 0 || importDuplicates) && (
              <Box sx={{ mb: 1.5 }}>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{
                    color: importDuplicates ? "success.main" : "error.main",
                  }}
                >
                  {importDuplicates
                    ? "Repeated mobile numbers are being imported — each person is listed with whoever shares their number:"
                    : `${mobileDuplicates.length} row${
                        mobileDuplicates.length === 1 ? "" : "s"
                      } skipped — that mobile number already belongs to somebody else:`}
                </Typography>

                {!importDuplicates && (
                  <RowTable
                    rows={mobileDuplicates}
                    lastHeader="Already used by"
                  />
                )}

                {allowDuplicateMobile ? (
                  <Box
                    onClick={() => setImportDuplicates((v) => !v)}
                    sx={{
                      mt: 1,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 1,
                      px: 1.5,
                      py: 0.75,
                      borderRadius: 999,
                      cursor: "pointer",
                      userSelect: "none",
                      border: `1px solid ${
                        importDuplicates ? "#15803D" : "#CBD5E1"
                      }`,
                      bgcolor: importDuplicates ? "#ECFDF5" : "#fff",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor: importDuplicates ? "#15803D" : PRIMARY,
                      },
                    }}
                  >
                    <Switch
                      size="small"
                      checked={importDuplicates}
                      onChange={(e) => setImportDuplicates(e.target.checked)}
                      onClick={(e) => e.stopPropagation()}
                      color="success"
                    />
                    <Box>
                      <Typography
                        sx={{
                          fontSize: "0.8rem",
                          fontWeight: 700,
                          color: importDuplicates ? "#15803D" : "#334155",
                        }}
                      >
                        Create customers with duplicate mobile numbers
                      </Typography>
                      <Typography
                        sx={{ fontSize: "0.7rem", color: "text.secondary" }}
                      >
                        For families that share one phone. Tell them apart later
                        by Aadhar or PAN.
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Alert
                    severity="info"
                    sx={{ mt: 1, borderRadius: 2, py: 0.25 }}
                  >
                    <Typography sx={{ fontSize: "0.78rem" }}>
                      To import these, switch on{" "}
                      <b>Allow duplicate mobile numbers</b> in Pawn Settings.
                      Without it the server refuses a repeated number, so they
                      cannot be created from here.
                    </Typography>
                  </Alert>
                )}
              </Box>
            )}

            {alreadyImported.length > 0 && (
              <Box sx={{ mb: 1.5 }}>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{ color: "text.secondary" }}
                >
                  {alreadyImported.length} row
                  {alreadyImported.length === 1 ? " was" : "s were"} imported on
                  an earlier run — matched by Old Customer ID and left exactly
                  as they are. Nothing to fix; this is what makes re-uploading
                  the same sheet safe.
                </Typography>
                <RowTable
                  rows={alreadyImported}
                  lastHeader="Already in the app as"
                />
              </Box>
            )}

            {partyDuplicates.length > 0 && (
              <Box sx={{ mb: 1.5 }}>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{ color: "error.main" }}
                >
                  {partyDuplicates.length} row
                  {partyDuplicates.length === 1 ? "" : "s"} skipped — a party
                  code can only belong to one customer, and unlike a mobile
                  number that is enforced by the database. Change or clear the
                  code in the sheet:
                </Typography>
                <RowTable rows={partyDuplicates} lastHeader="Already used by" />
              </Box>
            )}

            {otherProblems.length > 0 && (
              <Box sx={{ mb: 1.5 }}>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{ color: "error.main" }}
                >
                  {otherProblems.length} row
                  {otherProblems.length === 1 ? "" : "s"} skipped — fix{" "}
                  {otherProblems.length === 1 ? "it" : "them"} in the sheet and
                  upload again if you need{" "}
                  {otherProblems.length === 1 ? "it" : "them"}:
                </Typography>
                <RowTable
                  rows={otherProblems}
                  lastHeader="Why it will be skipped"
                />
              </Box>
            )}

            {warned.length > 0 && (
              <Box sx={{ mb: 1.5 }}>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{ color: "warning.main" }}
                >
                  {warned.length} of the {workTotal} row
                  {workTotal === 1 ? "" : "s"} being imported{" "}
                  {warned.length === 1 ? "has" : "have"} something worth a look.
                  This is not a list of everything being imported —{" "}
                  {warned.length === 1 ? "it is" : "they are"} already counted
                  above and will be created:
                </Typography>
                <RowTable rows={warned} lastHeader="Worth a look" />
              </Box>
            )}

            {analysis.skipped.length > 0 && (
              <Link
                component="button"
                type="button"
                onClick={() =>
                  downloadRowReport(
                    analysis.skipped,
                    "Pawn_Bulk_Customer_Skipped_Rows.xlsx",
                  )
                }
                sx={{
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  display: "block",
                  mb: 1.5,
                }}
              >
                Download the {analysis.skipped.length} skipped row
                {analysis.skipped.length === 1 ? "" : "s"} as Excel
              </Link>
            )}

            <Alert severity="info" sx={{ borderRadius: 2 }}>
              Customers are created first, then each nominee is linked to their
              customer — so the order of rows in your sheet does not matter.
              Bulk import skips OTP verification even when it is switched on in
              Pawn Settings, and document images cannot come from a spreadsheet
              (only the PAN / Aadhar / Other numbers are imported).
            </Alert>
          </Box>
        )}

        {/* ── Live progress ── */}
        {isCreating && (
          <Box
            sx={{
              mt: 2,
              p: { xs: 1.75, md: 2.25 },
              borderRadius: 3,
              bgcolor: "#fff",
              border: `1px solid ${PRIMARY}26`,
              boxShadow: `0 8px 24px ${PRIMARY}14`,
              animation: `${riseIn} 0.3s ease both`,
            }}
          >
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ mb: 1.75 }}
            >
              <ProgressRing value={pct} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  sx={{ color: PRIMARY }}
                >
                  {progress.phase === "nominees"
                    ? "Step 2 of 2 · Linking nominees"
                    : "Step 1 of 2 · Creating customers"}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    color: "#334155",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {progress.current || "Starting…"}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {progress.done} of {progress.total} rows processed
                </Typography>
                <Box
                  sx={{
                    mt: 0.75,
                    height: 8,
                    borderRadius: 4,
                    overflow: "hidden",
                    background: `linear-gradient(90deg, ${PRIMARY}14 25%, ${PRIMARY}26 37%, ${PRIMARY}14 63%)`,
                    backgroundSize: "420px 100%",
                    animation: `${shimmer} 1.5s linear infinite`,
                  }}
                >
                  <LinearProgress
                    variant="determinate"
                    value={pct}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: "transparent",
                      "& .MuiLinearProgress-bar": {
                        borderRadius: 4,
                        transition: "transform 0.4s ease",
                      },
                    }}
                  />
                </Box>
              </Box>
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <StatTile
                label="rows in sheet"
                value={progress.total}
                tone="slate"
              />
              <StatTile label="processed" value={progress.done} tone="blue" />
              <StatTile
                label={`customers · of ${progress.custTotal}`}
                value={progress.custOk}
                tone="green"
                icon={<PersonRoundedIcon />}
              />
              <StatTile
                label={`nominees · of ${progress.nomTotal}`}
                value={progress.nomOk}
                tone="green"
                icon={<DiversityRoundedIcon />}
              />
              <StatTile
                label="errors"
                value={progress.custFail + progress.nomFail}
                tone={progress.custFail + progress.nomFail ? "red" : "slate"}
                icon={<ErrorOutlineRoundedIcon />}
              />
            </Stack>

            <Typography
              variant="caption"
              sx={{ color: "text.secondary", display: "block", mt: 1 }}
            >
              Please keep this dialog open until it finishes.
            </Typography>
          </Box>
        )}

        {/* ── Result ── */}
        {result && !isCreating && (
          <Box sx={{ mt: 1 }}>
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              sx={{ mb: 1.5 }}
            >
              <StatTile
                label="customers created"
                value={result.custOk}
                tone="green"
                icon={<PersonRoundedIcon />}
                delay={0}
              />
              <StatTile
                label="nominees linked"
                value={result.nomOk}
                tone="green"
                icon={<DiversityRoundedIcon />}
                delay={70}
              />
              <StatTile
                label="failed"
                value={result.failures.length}
                tone={result.failures.length ? "red" : "slate"}
                icon={<ErrorOutlineRoundedIcon />}
                delay={140}
              />
              <StatTile
                label="skipped"
                value={analysis?.skipped?.length || 0}
                tone={analysis?.skipped?.length ? "amber" : "slate"}
                delay={210}
              />
            </Stack>

            <Alert
              severity={result.failures.length ? "warning" : "success"}
              icon={
                result.failures.length ? undefined : <CheckCircleRoundedIcon />
              }
              sx={{ borderRadius: 2, mb: 1.5 }}
            >
              <b>{result.custOk}</b> customer{result.custOk === 1 ? "" : "s"}{" "}
              created and <b>{result.nomOk}</b> nominee
              {result.nomOk === 1 ? "" : "s"} linked.
              {result.failures.length > 0 && (
                <>
                  {" "}
                  <b>{result.failures.length}</b> row
                  {result.failures.length === 1 ? "" : "s"} could not be
                  imported.
                </>
              )}
              {analysis?.skipped?.length > 0 && (
                <>
                  {" "}
                  {analysis.skipped.length} row
                  {analysis.skipped.length === 1 ? " was" : "s were"} skipped
                  before the import
                  {mobileDuplicates.length > 0
                    ? ` (${mobileDuplicates.length} for a mobile number already in use)`
                    : ""}
                  .
                </>
              )}
            </Alert>

            {result.failures.length > 0 && (
              <>
                <RowTable
                  rows={result.failures}
                  lastHeader="Reason"
                  maxHeight={220}
                />
                <Link
                  component="button"
                  type="button"
                  onClick={() =>
                    downloadRowReport(
                      [...result.failures, ...(analysis?.skipped || [])],
                      "Pawn_Bulk_Customer_Not_Imported.xlsx",
                    )
                  }
                  sx={{ mt: 1, fontSize: "0.8rem", fontWeight: 600 }}
                >
                  Download every row that did not make it, as Excel
                </Link>
              </>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.75 }}>
        {result && !isCreating ? (
          <>
            <Button onClick={resetAll} sx={{ textTransform: "none" }}>
              Import another sheet
            </Button>
            <Button
              variant="contained"
              onClick={onClose}
              sx={{ textTransform: "none", fontWeight: 600, bgcolor: PRIMARY }}
            >
              Done
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={handleClose}
              disabled={isCreating}
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<GroupAddRoundedIcon />}
              onClick={handleCreate}
              disabled={isCreating || isParsing || !workTotal}
              sx={{ textTransform: "none", fontWeight: 600, bgcolor: PRIMARY }}
            >
              {workTotal
                ? `Create ${analysis.customers.length} Customer${
                    analysis.customers.length === 1 ? "" : "s"
                  }${
                    analysis.nominees.length
                      ? ` + ${analysis.nominees.length} Nominee${
                          analysis.nominees.length === 1 ? "" : "s"
                        }`
                      : ""
                  }`
                : "Create Customers"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkCustomerDialog;
